import { useState } from 'react'
import {
  CheckCircle2,
  Copy,
  DollarSign,
  Gift,
  Link2,
  Mail,
  Users,
} from 'lucide-react'

import { Button } from '@/components'
import { cn } from '@/utils/cn'

type Referral = {
  id: string
  name: string
  email: string
  status: 'enrolled' | 'registered'
  date: string
  reward: string
}

const REFERRALS: Referral[] = [
  {
    id: '1',
    name: 'John Smith',
    email: 'john.smith@gmail.com',
    status: 'enrolled',
    date: '8 Jan 2026',
    reward: '$50',
  },
  {
    id: '2',
    name: 'Emily Brown',
    email: 'emily.brown@gmail.com',
    status: 'registered',
    date: '18 Jan 2026',
    reward: '$25',
  },
] as const

const REFERRAL_LINK =
  'https://christiancodeorgeMPA/ref/CHRISS04'.toLowerCase()

export const ParentRewards: React.FC = () => {
  const [copied, setCopied] = useState(false)
  const [copiedCode, setCopiedCode] = useState(false)

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(REFERRAL_LINK)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const handleCopyCode = async () => {
    try {
      await navigator.clipboard.writeText('CHRIS2026')
      setCopiedCode(true)
      setTimeout(() => setCopiedCode(false), 2000)
    } catch {
      setCopiedCode(true)
      setTimeout(() => setCopiedCode(false), 2000)
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Referral Rewards</h1>
        <p className="mt-1 text-sm text-gray-500">
          Share the love of learning and earn discounts.
        </p>
      </div>

      {/* Summary cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="flex items-center justify-between rounded-2xl bg-[#F28C28] px-6 py-5 text-white shadow-sm">
          <div>
            <p className="text-xs font-medium opacity-90">Total Earned</p>
            <p className="mt-2 text-2xl font-bold">$50</p>
            <p className="mt-1 text-[11px] opacity-90">In discounts</p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
            <DollarSign className="h-5 w-5" />
          </div>
        </div>

        <div className="flex items-center justify-between rounded-2xl bg-[#1D5AA6] px-6 py-5 text-white shadow-sm">
          <div>
            <p className="text-xs font-medium opacity-90">Pending Rewards</p>
            <p className="mt-2 text-2xl font-bold">2</p>
            <p className="mt-1 text-[11px] opacity-90">In progress</p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
            <Gift className="h-5 w-5" />
          </div>
        </div>

        <div className="flex items-center justify-between rounded-2xl bg-[#34A853] px-6 py-5 text-white shadow-sm">
          <div>
            <p className="text-xs font-medium opacity-90">Successful Referrals</p>
            <p className="mt-2 text-2xl font-bold">1</p>
            <p className="mt-1 text-[11px] opacity-90">Students enrolled</p>
          </div>
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15">
            <Users className="h-5 w-5" />
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className="space-y-4 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <h2 className="text-sm font-semibold text-gray-900">How it works</h2>
        <div className="grid gap-6 md:grid-cols-3">
          {[
            {
              step: 1,
              title: 'Share link',
              description: 'Send your unique referral link to friends and family.',
            },
            {
              step: 2,
              title: 'Enroll',
              description:
                'They sign up and enroll their child using your link.',
            },
            {
              step: 3,
              title: 'You earn',
              description:
                'You get 10% discount on your next enrollment.',
            },
          ].map(item => (
            <div
              key={item.step}
              className="flex flex-col items-center gap-3 text-center"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#F28C28]/10 text-xs font-semibold text-[#F28C28]">
                {item.step}
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium text-gray-900">
                  {item.title}
                </p>
                <p className="text-xs text-gray-500">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Referral link & code */}
      <div className="space-y-5 rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="space-y-3">
          <p className="text-sm font-semibold text-gray-900">
            Your referral link
          </p>
          <div className="flex flex-col gap-3 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Link2 className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                readOnly
                value={REFERRAL_LINK}
                className="w-full rounded-xl border border-gray-200 bg-gray-50 px-9 py-2.5 text-xs text-gray-700 shadow-sm outline-none"
              />
            </div>
            <Button
              type="button"
              variant="primary"
              className="mt-1 inline-flex items-center gap-1.5 rounded-xl bg-[#F28C28] px-4 py-2 text-xs font-semibold shadow-sm hover:bg-amber-600 focus:ring-amber-500 md:mt-0"
              icon={
                copied ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )
              }
              onClick={handleCopyLink}
            >
              {copied ? 'Copied' : 'Copy link'}
            </Button>
          </div>
        </div>

        <div className="space-y-3 pt-2 border-t border-gray-100">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="space-y-1">
              <p className="text-sm font-semibold text-gray-900">
                Your referral code
              </p>
              <p className="inline-flex items-center rounded-lg bg-gray-50 px-3 py-1 text-xs font-semibold text-gray-900">
                CHRIS2026
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-50"
              icon={
                copiedCode ? (
                  <CheckCircle2 className="h-3.5 w-3.5" />
                ) : (
                  <Copy className="h-3.5 w-3.5" />
                )
              }
              onClick={handleCopyCode}
            >
              {copiedCode ? 'Code copied' : 'Copy code'}
            </Button>
          </div>

          <div className="space-y-2">
            <p className="text-xs font-medium text-gray-700">Share via</p>
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Email', icon: Mail, color: 'bg-[#2563EB]' },
                { label: 'Facebook', color: 'bg-[#1877F2]' },
                { label: 'WhatsApp', color: 'bg-[#22C55E]' },
                { label: 'Twitter', color: 'bg-[#0EA5E9]' },
                { label: 'LinkedIn', color: 'bg-[#2563EB]' },
              ].map(item => {
                const Icon = item.icon ?? Mail
                return (
                  <button
                    key={item.label}
                    type="button"
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[11px] font-medium text-white',
                      item.color
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {item.label}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Referral history */}
      <div className="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              Referral history
            </h2>
            <p className="mt-1 text-xs text-gray-500">
              Track your referrals and earned rewards.
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-xs text-gray-700">
            <thead>
              <tr className="border-b border-gray-100 text-[11px] text-gray-500">
                <th className="py-2 pr-4 font-medium">Name</th>
                <th className="py-2 pr-4 font-medium">Email</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Date</th>
                <th className="py-2 pr-4 font-medium text-right">Reward</th>
              </tr>
            </thead>
            <tbody>
              {REFERRALS.map(referral => (
                <tr
                  key={referral.id}
                  className="border-b border-gray-50 last:border-0"
                >
                  <td className="py-2.5 pr-4 text-xs text-gray-900">
                    {referral.name}
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-gray-600">
                    {referral.email}
                  </td>
                  <td className="py-2.5 pr-4">
                    <span
                      className={cn(
                        'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-medium',
                        referral.status === 'enrolled'
                          ? 'bg-emerald-50 text-emerald-700'
                          : 'bg-amber-50 text-amber-700'
                      )}
                    >
                      {referral.status === 'enrolled' ? 'Enrolled' : 'Registered'}
                    </span>
                  </td>
                  <td className="py-2.5 pr-4 text-xs text-gray-600">
                    {referral.date}
                  </td>
                  <td className="py-2.5 pr-4 text-right text-xs font-semibold text-gray-900">
                    {referral.reward}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

